import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { checkConnection } from '../lib/core';

const { FiLogOut, FiUser, FiList, FiHome, FiUsers, FiDollarSign, FiSearch, FiSettings, FiActivity, FiMenu, FiX, FiGlobe, FiDatabase, FiCheckCircle, FiAlertCircle } = FiIcons;

const DBStatus = () => {
  const [status, setStatus] = useState('checking');
  useEffect(() => {
    checkConnection().then(isConnected => setStatus(isConnected ? 'connected' : 'disconnected'));
  }, []);
  if (status === 'checking') return null;
  return (
    <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-full flex items-center gap-2 text-[10px] font-black uppercase shadow-lg backdrop-blur-md z-50 border ${status === 'connected' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'bg-red-500/10 text-red-600 border-red-500/20'}`}>
      <FiDatabase className="text-sm" />
      <span>{status === 'connected' ? 'System Online' : 'DB Offline'}</span>
      {status === 'connected' ? <FiCheckCircle /> : <FiAlertCircle />}
    </div>
  );
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const NavItem = ({ to, icon: Icon, label }) => {
    const isActive = location.pathname === to;
    return (
      <button onClick={() => navigate(to)} className={`w-full flex items-center gap-4 px-6 py-3.5 rounded-2xl transition-all duration-300 group ${isActive ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-900'}`}>
        <div className={`p-2 rounded-xl transition-colors ${isActive ? 'bg-blue-50' : 'group-hover:bg-gray-100'}`}>
          <Icon className="text-xl" />
        </div>
        <span className="font-bold tracking-tight">{label}</span>
      </button>
    );
  };

  const Navigation = () => (
    <nav className="flex-1 space-y-1 px-2 mt-4 overflow-y-auto no-scrollbar">
      {user?.role === 'super_admin' && (
        <>
          <NavItem to="/admin" icon={FiHome} label="System Control" />
          <NavItem to="/admin/discovery" icon={FiGlobe} label="Pilot Directory" />
          <NavItem to="/master/drops" icon={FiSettings} label="Global Pricing" />
          <NavItem to="/admin/settings" icon={FiSettings} label="System Logs" />
        </>
      )}
      {user?.role === 'master' && (
        <>
          <NavItem to="/master" icon={FiHome} label="Overview" />
          <NavItem to="/master/pilots" icon={FiUsers} label="Team Roster" />
          <NavItem to="/master/discovery" icon={FiSearch} label="Recruit Pilots" />
          <NavItem to="/master/activity" icon={FiActivity} label="Ledger" />
          <NavItem to="/master/drops" icon={FiSettings} label="Drops Config" />
          <NavItem to="/master/payouts" icon={FiDollarSign} label="Payouts" />
        </>
      )}
      {user?.role === 'pilot' && (
        <>
          <NavItem to="/pilot" icon={FiHome} label="Dashboard" />
          <NavItem to="/pilot/log" icon={FiList} label="Log Drop" />
        </>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f5f5f7]">
      <header className="lg:hidden flex items-center justify-between p-4 bg-white/80 backdrop-blur-md border-b sticky top-0 z-[60]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center text-white font-black text-xl">R</div>
          <span className="font-black text-gray-900 tracking-tighter">ROX Tracker</span>
        </div>
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 bg-gray-100 rounded-xl text-gray-600 relative z-[70]">
          {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </header>

      <aside className={`fixed inset-y-0 left-0 z-50 w-72 p-6 flex-col bg-[#f5f5f7] transition-transform duration-500 lg:translate-x-0 lg:static lg:flex ${isMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="bg-white/50 rounded-[2.5rem] flex-1 p-4 flex flex-col border border-white/40 shadow-sm backdrop-blur-sm overflow-hidden">
          <div className="px-4 py-8 flex items-center gap-4 shrink-0">
            <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-white font-black text-2xl">R</div>
            <div>
              <h1 className="font-black text-gray-900 leading-none">ROX Tracker</h1>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 italic">Boss Hunt OS</p>
            </div>
          </div>
          <Navigation />
          <div className="p-4 bg-white/40 rounded-3xl border border-white/50 mt-4 shrink-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-400 border border-gray-100"><FiUser /></div>
              <p className="text-xs font-black text-gray-900 truncate">@{user?.username}</p>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} className="w-full py-3 text-[10px] font-black text-red-500 uppercase tracking-widest hover:bg-red-50 rounded-2xl transition-all">
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-5 md:p-12 min-h-screen relative z-10 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
      <DBStatus />
    </div>
  );
};

export default Layout;